/**
 * A door or a window resized by its edges in 3D and on the wall plan: the app's resize markers of an opening
 * (OpeningFurnitureController.GetBaseResizeMarkers), shown in its resize mode («Изменить размер»). Every opening
 * has its left and right sides, its top and its two top corners; a window also its bottom and bottom corners, which
 * move its sill — a door or an arch stands on the floor and has none there (`IsDoorOrArch`).
 */
import type { Level, Opening } from '../core/hp/types';
import type { EditResult } from '../core/geometry/edit';
import { editProperties } from './propertyEdit';
import { snapOpeningWidth } from './openingProperties';
import { t } from '../i18n/I18n';

export type OpeningEdge = 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
/** An opening as a box on its wall: along the wall from its start (`t0` to `t1`), up from the floor (its sill `h0` to its top `h1`). */
export interface OpeningBox { t0: number; t1: number; h0: number; h1: number }

/** The smallest width and height a drag leaves; an opening already smaller keeps what it has. */
export const OPENING_MIN_SIZE = .1;
/** What the wall keeps between openings side by side and to its own ends (validateLevel). */
const GAP = .02;

/** The edges an opening is resized by: the bottom ones only for one with a sill to move. */
export function openingEdges(withSill: boolean): OpeningEdge[] {
  const top: OpeningEdge[] = ['left', 'right', 'top', 'top-left', 'top-right'];
  return withSill ? [...top, 'bottom', 'bottom-left', 'bottom-right'] : top;
}
/** Which sides of the box an edge's handle moves. */
export function edgeSides(edge: OpeningEdge): { left: boolean; right: boolean; top: boolean; bottom: boolean } {
  return { left: edge.endsWith('left'), right: edge.endsWith('right'), top: edge.startsWith('top'), bottom: edge.startsWith('bottom') };
}
export function openingBox(opening: Pick<Opening, 'offset' | 'width' | 'sill' | 'height'>): OpeningBox {
  return { t0: opening.offset - opening.width / 2, t1: opening.offset + opening.width / 2, h0: opening.sill, h1: opening.sill + opening.height };
}
/** Where an edge's handle is on the box, along the wall and up: a side's middle, or a corner. */
export function edgePoint(box: OpeningBox, edge: OpeningEdge): { t: number; h: number } {
  const sides = edgeSides(edge);
  return { t: sides.left ? box.t0 : sides.right ? box.t1 : (box.t0 + box.t1) / 2, h: sides.top ? box.h1 : sides.bottom ? box.h0 : (box.h0 + box.h1) / 2 };
}

/**
 * The opening `id` with the sides its `edge` moves taken to `target` (along the wall, up from the floor), as one
 * property edit: its door's twin on the other room's wall and its curtains follow it (`editProperties`). The sides
 * keep inside the wall — `GAP` from its ends, the top under its top, the sill on the floor — and clear of the openings
 * beside, above and below it, as the wall keeps them (validateLevel); the width, the height and the sill stick to whole
 * 10 and 50 cm at `scale` metres per screen pixel as a width does on the plan (`snapOpeningWidth`), else land on whole
 * centimetres; `free` (⌘ or Ctrl held) lets them go. A place the wall still refuses returns its error.
 */
export function previewOpeningEdge(base: Level, id: string, edge: OpeningEdge, target: { t: number; h: number }, options: { scale?: number; free?: boolean } = {}): EditResult {
  const opening = base.openings.find(value => value.id === id), wall = base.walls.find(value => value.id === opening?.wallId);
  if (!opening || !wall) return { error: t('#GeometryOpeningNotFound') };
  if (!Number.isFinite(target.t) || !Number.isFinite(target.h)) return { error: t('#GeometryCheckOpeningSize') };
  const sides = edgeSides(edge), box = openingBox(opening), next = { ...box };
  const minWidth = Math.min(OPENING_MIN_SIZE, opening.width), minHeight = Math.min(OPENING_MIN_SIZE, opening.height);
  const stick = (size: number) => {
    const held = options.free || !options.scale ? size : snapOpeningWidth(size, options.scale);
    return held === size ? Math.round(size * 100) / 100 : held;
  };
  const others = base.openings.filter(other => other.id !== id && other.wallId === wall.id).map(openingBox);
  // Along the wall first, against what stands beside it at its height; a neighbour it already overlaps (an imported
  // fault) bounds nothing.
  if (sides.left || sides.right) {
    let low = GAP, high = wall.length - GAP;
    for (const other of others) {
      if (Math.min(box.h1, other.h1) - Math.max(box.h0, other.h0) <= 1e-3) continue;
      if (other.t1 <= box.t0 + 1e-6) low = Math.max(low, other.t1 + GAP);
      else if (other.t0 >= box.t1 - 1e-6) high = Math.min(high, other.t0 - GAP);
    }
    if (sides.left) next.t0 = box.t1 - clamp(stick(box.t1 - target.t), minWidth, Math.max(minWidth, box.t1 - Math.min(low, box.t0)));
    else next.t1 = box.t0 + clamp(stick(target.t - box.t0), minWidth, Math.max(minWidth, Math.max(high, box.t1) - box.t0));
  }
  // Then up and down, against what stands above and under it along its new width.
  if (sides.top || sides.bottom) {
    let low = 0, high = wall.height;
    for (const other of others) {
      if (Math.min(next.t1, other.t1) - Math.max(next.t0, other.t0) <= -GAP + 1e-6) continue;
      if (other.h1 <= box.h0 + 1e-6) low = Math.max(low, other.h1);
      else if (other.h0 >= box.h1 - 1e-6) high = Math.min(high, other.h0);
    }
    if (sides.top) next.h1 = box.h0 + clamp(stick(target.h - box.h0), minHeight, Math.max(minHeight, Math.max(high, box.h1) - box.h0));
    else next.h0 = clamp(stick(target.h), Math.min(low, box.h0), box.h1 - minHeight);
  }
  const round = (value: number) => Math.round(value * 1e4) / 1e4;
  const values = { offset: round((next.t0 + next.t1) / 2), width: round(next.t1 - next.t0), sill: round(next.h0), height: round(next.h1 - next.h0) };
  if (values.offset === opening.offset && values.width === opening.width && values.sill === opening.sill && values.height === opening.height) return { level: base };
  return editProperties(base, { kind: 'opening', id, values }, base);
}

function clamp(value: number, low: number, high: number): number { return value < low ? low : value > high ? high : value; }
